import React, { useState } from 'react';
import './SnapshotModal.css';
import '../../css/TaskReference.css';

const SnapshotModal = ({ isOpen, onClose, snapshot, parentInfo = null }) => {
    const [copiedField, setCopiedField] = useState(null);

    if (!isOpen || !snapshot) return null;

    // Format date for display
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    // Copy text to clipboard
    const copyToClipboard = async (text, fieldName) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    // Copy formatted text for Quill editor
    const copyFormattedForQuill = async (htmlContent, fieldName) => {
        try {
            const plainText = stripHtmlTags(htmlContent);
            
            // Use the advanced Clipboard API to write both formats
            if (navigator.clipboard && navigator.clipboard.write) {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([htmlContent], { type: 'text/html' }),
                        'text/plain': new Blob([plainText], { type: 'text/plain' })
                    })
                ]);
            } else {
                // Fallback to simple text copy if advanced API not supported
                await navigator.clipboard.writeText(htmlContent);
            }
            
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (error) {
            console.error('Failed to copy formatted content:', error);
            // Fallback to plain HTML copy
            await copyToClipboard(htmlContent, fieldName);
        }
    };

    // Strip HTML tags for display
    const stripHtmlTags = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').trim();
    };

    return (
        <div className="snapshot-modal-overlay" onClick={onClose}>
            <div className="snapshot-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="snapshot-modal-header">
                    <h2>Document Snapshot</h2>
                    <button className="snapshot-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="snapshot-modal-body">
                    <div className="snapshot-field-grid">
                        {/* Document Name */}
                        <div className="snapshot-field">
                            <label>Document Name:</label>
                            <div className="snapshot-field-content">
                                <span className="snapshot-field-value">
                                    {snapshot.document_name || 'Untitled Document'}
                                </span>
                                <button 
                                    className="copy-button"
                                    onClick={() => copyToClipboard(snapshot.document_name || 'Untitled Document', 'name')}
                                >
                                    {copiedField === 'name' ? '✓' : <i className="fa-solid fa-copy"></i>}
                                </button>
                            </div>
                        </div>

                        {/* Document Type */}
                        <div className="snapshot-field">
                            <label>Document Type:</label>
                            <div className="snapshot-field-content">
                                <span className="snapshot-field-value document-type">
                                    {snapshot.document_type}
                                </span>
                            </div>
                        </div>

                        {/* Parent Relationships - Enhanced display */}
                        {(parentInfo && parentInfo.length > 0) || snapshot.document_parent_page ? (
                            <div className="snapshot-field">
                                <label>Parent Relationships:</label>
                                <div className="snapshot-field-content">
                                    <div className="parent-relationships">
                                        {parentInfo && parentInfo.length > 0 ? (
                                            parentInfo.map((parent, idx) => (
                                                <div key={idx} className="parent-relationship-item">
                                                    <span className="snapshot-field-value parent-page">
                                                        {parent.name} ({parent.type})
                                                    </span>
                                                    <button 
                                                        className="copy-button"
                                                        onClick={() => copyToClipboard(parent.name, `parent-${idx}`)}
                                                    >
                                                        {copiedField === `parent-${idx}` ? '✓' : <i className="fa-solid fa-copy"></i>}
                                                    </button>
                                                </div>
                                            ))
                                        ) : snapshot.document_parent_page ? (
                                            <div className="parent-relationship-item">
                                                <span className="snapshot-field-value parent-page">
                                                    📄 {snapshot.document_parent_page}
                                                </span>
                                                <button 
                                                    className="copy-button"
                                                    onClick={() => copyToClipboard(snapshot.document_parent_page, 'parent')}
                                                >
                                                    {copiedField === 'parent' ? '✓' : <i className="fa-solid fa-copy"></i>}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {/* Created Date */}
                        <div className="snapshot-field">
                            <label>Created:</label>
                            <div className="snapshot-field-content">
                                <span className="snapshot-field-value">
                                    {formatDate(snapshot.created_at)}
                                </span>
                            </div>
                        </div>

                        {/* Status */}
                        <div className="snapshot-field">
                            <label>Status:</label>
                            <div className="snapshot-field-content">
                                <span className={`snapshot-field-value status ${snapshot.completed ? 'completed' : 'in-progress'}`}>
                                    {snapshot.completed ? '✓ Document Created' : '○ Draft'}
                                </span>
                            </div>
                        </div>

                        {/* Visibility */}
                        <div className="snapshot-field">
                            <label>Visibility:</label>
                            <div className="snapshot-field-content">
                                <span className={`snapshot-field-value visibility ${snapshot.is_public ? 'public' : 'private'}`}>
                                    {snapshot.is_public ? '🌐 Public' : '🔒 Private'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    {snapshot.content && (
                        <div className="snapshot-content-section">
                            <div className="snapshot-content-header">
                                <label>Content:</label>
                                <div className="content-actions">
                                    <button 
                                        className="copy-button copy-button-large"
                                        onClick={() => copyFormattedForQuill(snapshot.content, 'formatted')}
                                        title="Copy with formatting preserved for Quill editor"
                                    >
                                        {copiedField === 'formatted' ? '✓ Copied for Quill' : '✨ Copy for Quill Editor'}
                                    </button>
                                    <button 
                                        className="copy-button copy-button-large"
                                        onClick={() => copyToClipboard(snapshot.content, 'html')}
                                    >
                                        {copiedField === 'html' ? '✓ Copied HTML' : '📋 Copy HTML'}
                                    </button>
                                    <button 
                                        className="copy-button copy-button-large"
                                        onClick={() => copyToClipboard(stripHtmlTags(snapshot.content), 'text')}
                                    >
                                        {copiedField === 'text' ? '✓ Copied Text' : '📄 Copy Text Only'}
                                    </button>
                                </div>
                            </div>
                            
                            <div className="snapshot-content-preview">
                                <div className="content-tabs">
                                    <button className="content-tab active">📖 Preview</button>
                                    <button className="content-tab">🔧 HTML</button>
                                </div>
                                
                                <div className="content-display">
                                    <div className="content-preview" 
                                         dangerouslySetInnerHTML={{ __html: snapshot.content }}>
                                    </div>
                                    <div className="content-html" style={{ display: 'none' }}>
                                        <pre><code>{snapshot.content}</code></pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Description Section */}
                    {snapshot.description && (
                        <div className="snapshot-field">
                            <label>Description:</label>
                            <div className="snapshot-field-content">
                                <span className="snapshot-field-value description">
                                    {snapshot.description}
                                </span>
                                <button 
                                    className="copy-button"
                                    onClick={() => copyToClipboard(snapshot.description, 'description')}
                                >
                                    {copiedField === 'description' ? '✓' : '📋'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="snapshot-modal-footer">
                    <button className="snapshot-modal-button secondary" onClick={onClose}>
                        Close
                    </button>
                    <button 
                        className="snapshot-modal-button primary"
                        onClick={() => copyToClipboard(JSON.stringify(snapshot, null, 2), 'all')}
                    >
                        {copiedField === 'all' ? '✓ Copied All Data' : '📋 Copy All Data (JSON)'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SnapshotModal;